import { Vue, Component, Prop, Watch, Emit } from 'vue-property-decorator';
import { toCamel } from 'snake-camel';

type EditHistoryGroup = {
    groupId: string | null;
    cvId: string;
    updated: string | null;
    comId: string | null;
    committeeName: string | null;
    pdId: string | null;
    producerName: string | null;
    sitemId: string | null;
    shippingItemName: string | null;
    pairSummary: string | null;
    recordCount: number | null;
};

type MasterOption = { id: string; name: string };
type ShippingItemOption = MasterOption & { comId: string | null };

type SortField = 'updated' | 'committeeName' | 'producerName' | 'shippingItemName' | 'pairSummary' | 'recordCount';

export type SelectedEditHistoryGroup = {
    groupId: string | null;
    cvId: string;
};

@Component
export default class CultivationHistoryEditHistoryModal extends Vue {
    @Prop()
    private value?: boolean;

    @Emit('input')
    input(value: boolean): void {
        value
    }

    @Prop()
    private tenantId?: string | null;

    @Prop()
    private isPesticidePlan?: boolean;

    @Prop()
    private pesticidePlanProducer?: boolean;

    @Prop()
    private initialComId?: string | null;

    @Prop()
    private initialPdId?: string | null;

    @Prop()
    private initialPdName?: string | null;

    @Prop()
    private initialSitemId?: string | null;

    private isLoading = false;
    private groups: EditHistoryGroup[] = [];

    private committeesMaster: MasterOption[] = [];
    private producersMaster: MasterOption[] = [];
    private shippingItemsMaster: ShippingItemOption[] = [];
    private producerOptionsNarrowed: MasterOption[] = [];

    private selectedComId = '';
    private selectedPdId = '';
    private selectedSitemId = '';

    private sortField: SortField = 'updated';
    private sortOrder: 'asc' | 'desc' = 'desc';

    get isModalVisible(): boolean {
        return this.value ? this.value : false;
    }

    set isModalVisible(value: boolean) {
        this.input(value);
    }

    get showProducerColumn(): boolean {
        return !!this.isPesticidePlan;
    }

    private mergeWithGroupData(master: MasterOption[], idKey: 'comId' | 'pdId' | 'sitemId', nameKey: 'committeeName' | 'producerName' | 'shippingItemName'): MasterOption[] {
        const extra = new Map<string, string>();
        this.groups.forEach(g => {
            const id = g[idKey];
            if (id && !master.some(m => m.id === id) && !extra.has(id)) {
                extra.set(id, g[nameKey] || '(削除済み)');
            }
        });
        return [...master, ...[...extra.entries()].map(([id, name]) => ({ id, name }))];
    }

    get committeeOptions(): MasterOption[] {
        return this.mergeWithGroupData(this.committeesMaster, 'comId', 'committeeName');
    }

    get producerOptions(): MasterOption[] {
        return this.mergeWithGroupData(this.producerOptionsNarrowed, 'pdId', 'producerName');
    }

    get shippingItemOptions(): MasterOption[] {
        const narrowed = this.shippingItemsMaster.filter(s => !this.selectedComId || s.comId === this.selectedComId);
        return this.mergeWithGroupData(narrowed, 'sitemId', 'shippingItemName');
    }

    get displayedGroups(): EditHistoryGroup[] {
        return this.groups;
    }

    @Watch('value')
    async whenOpen(): Promise<void> {
        if (this.value) {
            this.selectedComId = this.initialComId ?? '';
            this.selectedPdId = this.initialPdId ?? '';
            this.selectedSitemId = this.initialSitemId ?? '';
            await this.withLoading(async () => {
                await this.fetchMasters();
                if (this.showProducerColumn) await this.narrowProducers();
                await this.fetchGroups();
            });
        } else {
            this.reset();
        }
    }

    private async withLoading(fn: () => Promise<void>) {
        this.isLoading = true;
        try {
            await fn();
        } finally {
            this.isLoading = false;
        }
    }

    private async fetchMasters() {
        if (!this.tenantId) return;
        try {
            const comRes = toCamel(await this.$apiClient.doRequest('get', `/cultivation_histories/affiliated_committees/${this.tenantId}`)) as { committees: any[] };
            this.committeesMaster = (comRes.committees ?? []).map((c: any) => ({ id: c.id, name: c.name }));

            if (this.pesticidePlanProducer && !this.isPesticidePlan) {
                this.producersMaster = this.initialPdId ? [{ id: this.initialPdId, name: this.initialPdName || '' }] : [];
                this.producerOptionsNarrowed = this.producersMaster;
                this.shippingItemsMaster = await this.fetchShippingItemsForCommittees(this.committeesMaster.map(c => c.id));
            } else {
                const pdRes = await this.$apiClient.doRequest('get', `/producers/tenantId?with_hidden=true&tenant_id=${this.tenantId}`);
                this.producersMaster = (pdRes.producers ?? []).map((p: any) => ({ id: p.pdId, name: p.pdName }));
                const siRes = await this.$apiClient.doRequest('get', `/shipping_items/tenantId?with_hidden=true&tenant_id=${this.tenantId}`);
                this.shippingItemsMaster = (siRes.shippingItems ?? []).map((s: any) => ({ id: s.id, name: s.name, comId: s.comId }));
            }
        } catch {
            this.$toasted.error('データの取得に失敗しました。');
        }
    }

    private async fetchShippingItemsForCommittees(comIds: string[]): Promise<ShippingItemOption[]> {
        if (comIds.length === 0) return [];
        const results = await Promise.all(comIds.map(comId =>
            this.$apiClient.doRequest('get', `/shipping_items/tenantId?with_hidden=true&tenant_id=${this.tenantId}&com_id=${comId}`)
        ));
        const map = new Map<string, ShippingItemOption>();
        results.forEach(res => {
            (res.shippingItems ?? []).forEach((s: any) => {
                if (!map.has(s.id)) map.set(s.id, { id: s.id, name: s.name, comId: s.comId ?? null });
            });
        });
        return [...map.values()];
    }

    private async narrowProducers() {
        let base = this.producersMaster;
        try {
            if (this.selectedComId) {
                const res = await this.$apiClient.doRequest('get', `/committees/${this.selectedComId}`);
                const validPdIds = (res.committeeWorkers as any[] ?? []).map((w: any) => w.pd_id);
                base = base.filter(p => validPdIds.includes(p.id));
            }
            if (this.selectedSitemId) {
                const res = await this.$apiClient.doRequest('get', `/shipping_items/${this.selectedSitemId}`);
                const validPdIds = (res.producers as any[] ?? []).map((p: any) => p.id);
                base = base.filter(p => validPdIds.includes(p.id));
            }
        } catch {
            this.$toasted.error('データの取得に失敗しました。');
        }
        this.producerOptionsNarrowed = base;
    }

    private async fetchGroups() {
        if (!this.tenantId) return;
        try {
            const params = new URLSearchParams({ tenant_id: this.tenantId as string });
            if (this.selectedComId) params.set('com_id', this.selectedComId);
            if (this.selectedPdId) params.set('pd_id', this.selectedPdId);
            if (this.selectedSitemId) params.set('sitem_id', this.selectedSitemId);
            const res = await this.$apiClient.doRequest('get', `/cultivation_histories/edit_history?${params.toString()}`);
            this.groups = (toCamel(res) as EditHistoryGroup[]) ?? [];
            this.applySort();
        } catch {
            this.groups = [];
            this.$toasted.error('データの取得に失敗しました。');
        }
    }

    async onComChange() {
        this.selectedPdId = '';
        this.selectedSitemId = '';
        await this.withLoading(async () => {
            if (this.showProducerColumn) await this.narrowProducers();
            await this.fetchGroups();
        });
    }

    async onSitemChange() {
        this.selectedPdId = '';
        await this.withLoading(async () => {
            if (this.showProducerColumn) await this.narrowProducers();
            await this.fetchGroups();
        });
    }

    async onPdChange() {
        await this.withLoading(() => this.fetchGroups());
    }

    isSortActive(field: SortField): boolean {
        return this.sortField === field;
    }

    isSortAscending(field: SortField): boolean {
        return this.isSortActive(field) && this.sortOrder === 'asc';
    }

    isSortDescending(field: SortField): boolean {
        return this.isSortActive(field) && this.sortOrder === 'desc';
    }

    sortBy(field: SortField) {
        this.sortOrder = this.sortField === field && this.sortOrder === 'asc' ? 'desc' : 'asc';
        this.sortField = field;
        this.applySort();
    }

    private applySort() {
        const dir = this.sortOrder === 'asc' ? 1 : -1;
        const field = this.sortField;
        this.groups.sort((a, b) => {
            if (field === 'updated') {
                const dateDiff = ((a.updated ? new Date(a.updated).getTime() : 0) - (b.updated ? new Date(b.updated).getTime() : 0)) * dir;
                if (dateDiff !== 0) return dateDiff;
                return (b.recordCount ?? 0) - (a.recordCount ?? 0);
            }
            if (field === 'recordCount') {
                return ((a.recordCount ?? 0) - (b.recordCount ?? 0)) * dir;
            }
            return (a[field] ?? '').localeCompare(b[field] ?? '', 'ja') * dir;
        });
    }

    formatUpdated(value: string | null): string {
        return this.$apiClient.formatJst(value);
    }

    groupKey(group: EditHistoryGroup): string {
        return group.groupId ?? group.cvId;
    }

    pairSummaryLines(group: EditHistoryGroup): string[] {
        if (!group.pairSummary) return [];
        return group.pairSummary.split('、').filter(line => line.length > 0);
    }

    @Emit('selectedGroup')
    select(group: EditHistoryGroup): SelectedEditHistoryGroup {
        this.input(false);
        return { groupId: group.groupId, cvId: group.cvId };
    }

    private cancel() {
        this.input(false);
    }

    private reset() {
        this.isLoading = false;
        this.groups = [];
        this.committeesMaster = [];
        this.producersMaster = [];
        this.shippingItemsMaster = [];
        this.producerOptionsNarrowed = [];
        this.selectedComId = '';
        this.selectedPdId = '';
        this.selectedSitemId = '';
        this.sortField = 'updated';
        this.sortOrder = 'desc';
        (this.$refs.filterAccordion as HTMLElement | undefined)?.classList.remove('open');
    }
}
